import { api, apiService } from '@/services/core/ApiService'
import { whileSameSession } from '@/services/core/sessionGuard'
import { useCartStore } from '@/stores/useCartStore'
import { useZipCodeStore } from '@/stores/useZipCodeStore'
import {
    CalcCartShippingRequestSchema,
    CartShippingResponseSchema,
    type ICartShippingResponse,
    type ISelectCartShippingResponse,
    SelectCartShippingResponseSchema,
} from '~/schemas/cart/cart'
import { SelectShippingRequestSchema } from '~/schemas/shared/shipping'
import { cartErrorHandler } from './cartErrors'

/**
 * `POST cart/calc-shipping-maestro`: cota o frete do carrinho no CEP e grava no carrinho o preço e o prazo da primeira
 * cotação (a API recalcula cupom e totais). Na staging/api devolve também o frete por vendedor (`shipmentBySeller`), com
 * a opção valendo de cada um. Erro com a mensagem da API no toast ("Erro ao calcular o frete, tente
 * novamente"), menos o `cartNotFound`. Carrinho vazio sem login responde 401. Grava na store o carrinho devolvido e o
 * CEP como o preferido do visitante (`useZipCodeStore`).
 */
export async function calcCartShippingService(zipCode: string): Promise<ICartShippingResponse> {
    return apiService(api)
        .post(
            'cart/calc-shipping-maestro',
            () => CalcCartShippingRequestSchema.parse({ zipcode: zipCode }),
            CartShippingResponseSchema,
            { meta: { showLoader: true } },
        )
        .onSuccess(
            whileSameSession((response) => {
                useCartStore.getState().setCart(response.cart)
                useZipCodeStore.getState().setZipCode(zipCode)
            }),
        )
        .onError(cartErrorHandler, { useDefaultErrorHandler: false })
        .execute()
}

/**
 * `POST cart/select-shipping` (staging/api): escolhe a opção de frete de um vendedor. A API recota o carrinho inteiro e
 * devolve o carrinho com o frete novo e o detalhamento por vendedor; grava o carrinho na store. Sem o loader global: a
 * tela mostra o andamento na própria linha. 422 (a opção saiu da cotação) com a mensagem da API no toast; `cartNotFound`
 * troca o carrinho guardado, sem toast.
 */
export async function selectCartShippingService(
    sellerId: string,
    offerCode: string,
): Promise<ISelectCartShippingResponse> {
    return apiService(api)
        .post(
            'cart/select-shipping',
            () => SelectShippingRequestSchema.parse({ sellerId, offerCode }),
            SelectCartShippingResponseSchema,
        )
        .onSuccess(
            whileSameSession((response) => {
                useCartStore.getState().setCart(response.cart)
            }),
        )
        .onError(cartErrorHandler, { useDefaultErrorHandler: false })
        .execute()
}
